import _ from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import {
  FeatureFlag,
  ICoherenceReportSettings,
  IDataGridSettings,
  IDismissedMeetingNotification,
  IDrawerSettings,
  IHomeSettings,
  IMetricsSettings,
  ISearchSettings,
  ISettingsData,
  ISideMenuSettings,
  ITableSettings,
  ITeamImplementationSettings,
  ITeamMeetingSettings,
  ITeamStrategySettings,
  IUserImplementationSettings,
  IUserMeetingsSettings,
  IUserStrategySettings,
  useLocalStorage,
  type ITeamOverviewSettings,
  type IUserFeedbackSettings,
} from "src/hooks";
import {
  DEFAULT_DATAGRID_SETTINGS,
  DEFAULT_METRICS_SETTINGS,
  DEFAULT_SETTINGS_DATA,
  DEFAULT_TABLE_SETTINGS,
  DEFAULT_TEAM_IMPLEMENTATION_SETTINGS,
  DEFAULT_TEAM_MEETING_SETTINGS,
  DEFAULT_TEAM_STRATEGY_SETTINGS,
  DEFAULT_USER_FEEDBACK_SETTINGS,
  DEFAULT_USER_IMPLEMENTATION_SETTINGS,
  DEFAULT_USER_MEETINGS_SETTINGS,
  DEFAULT_USER_STRATEGY_SETTINGS,
} from "./constants";

declare global {
  interface Window {
    [key: `toggle${string}`]: () => void;
  }
}

interface ISettingsContext extends ISettingsData {
  updateHomeSettings: (settings: IHomeSettings) => void;
  updateSideMenuSettings: (settings: ISideMenuSettings) => void;
  updateDrawerSettings: (settings: IDrawerSettings) => void;
  updateTeamOverviewSettings: (settings: Partial<ITeamOverviewSettings>) => void;
  updateTeamStrategySettings: (planGuid: string, settings: Partial<ITeamStrategySettings>) => void;
  updateTeamImplementationSettings: (teamSlug: string, settings: Partial<ITeamImplementationSettings>) => void;
  updateTeamMetricsSettings: (teamSlug: string, settings: Partial<IMetricsSettings>) => void;
  updateTeamMeetingSettings: (teamSlug: string, settings: Partial<ITeamMeetingSettings>) => void;
  updateUserStrategySettings: (userGuid: string, settings: Partial<IUserStrategySettings>) => void;
  updateUserImplementationSettings: (userGuid: string, settings: Partial<IUserImplementationSettings>) => void;
  updateUserMetricsSettings: (userGuid: string, settings: Partial<IMetricsSettings>) => void;
  updateUserFeedbackSettings: (userGuid: string, settings: Partial<IUserFeedbackSettings>) => void;
  updateUserMeetingsSettings: (userGuid: string, settings: Partial<IUserMeetingsSettings>) => void;
  updateSearchSettings: (settings: ISearchSettings) => void;
  updateFeatureFlag: (featureFlag: FeatureFlag, value: boolean) => void;
  updateDataGridSettings: (dataGridId: string, settings: Partial<IDataGridSettings>) => void;
  updateTableSettings: (tableId: string, settings: Partial<ITableSettings>) => void;
  updateCoherenceReportSettings: (settings: Partial<ICoherenceReportSettings>) => void;
  updateDismissedMeetingNotifications: (notifications: IDismissedMeetingNotification[]) => void;
}

const SettingsContext = React.createContext<ISettingsContext>({
  ...DEFAULT_SETTINGS_DATA,
  updateHomeSettings: () => null,
  updateSideMenuSettings: () => null,
  updateDrawerSettings: () => null,
  updateTeamOverviewSettings: () => null,
  updateTeamStrategySettings: () => null,
  updateTeamImplementationSettings: () => null,
  updateTeamMetricsSettings: () => null,
  updateTeamMeetingSettings: () => null,
  updateUserStrategySettings: () => null,
  updateUserImplementationSettings: () => null,
  updateUserMetricsSettings: () => null,
  updateUserMeetingsSettings: () => null,
  updateUserFeedbackSettings: () => null,
  updateSearchSettings: () => null,
  updateFeatureFlag: () => null,
  updateDataGridSettings: () => null,
  updateTableSettings: () => null,
  updateCoherenceReportSettings: () => null,
  updateDismissedMeetingNotifications: () => null,
});

interface IProps {
  children?: React.ReactNode;
}

const SettingsProvider = (props: IProps) => {
  const { getLocalStorageSettings, setLocalStorageSettings } = useLocalStorage();
  const [settings, setSettings] = useState<ISettingsData>(DEFAULT_SETTINGS_DATA);

  useEffect(() => {
    const localStorageSettings = getLocalStorageSettings();

    if (localStorageSettings != null) {
      if (localStorageSettings.dismissedMeetingNotifications) {
        const now = Date.now();
        const ONE_DAY_MS = 24 * 60 * 60 * 1000;
        localStorageSettings.dismissedMeetingNotifications = localStorageSettings.dismissedMeetingNotifications.filter(
          (n) => n.timestamp && now - n.timestamp < ONE_DAY_MS,
        );
      }
      setSettings(_.merge(settings, localStorageSettings));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLocalStorageSettings(settings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  function updateTeamOverviewSettings(teamOverviewSettings: Partial<ITeamOverviewSettings>) {
    setSettings({
      ...settings,
      teamOverviewSettings: {
        ...settings.teamOverviewSettings,
        ...teamOverviewSettings,
      },
    });
  }

  function updateHomeSettings(homeSettings: IHomeSettings) {
    setSettings({
      ...settings,
      homeSettings: homeSettings,
    });
  }

  function updateSideMenuSettings(sideMenuSettings: ISideMenuSettings) {
    setSettings({
      ...settings,
      sideMenuSettings: sideMenuSettings,
    });
  }

  function updateDrawerSettings(drawerSettings: IDrawerSettings) {
    setSettings({
      ...settings,
      drawerSettings: drawerSettings,
    });
  }

  function updateTeamStrategySettings(teamSlug: string, teamStrategySettings: Partial<ITeamStrategySettings>) {
    setSettings({
      ...settings,
      teamStrategySettings: {
        ...settings.teamStrategySettings,
        [teamSlug]: {
          ...(settings.teamStrategySettings[teamSlug] ?? { ...DEFAULT_TEAM_STRATEGY_SETTINGS }),
          ...teamStrategySettings,
        },
      },
    });
  }

  function updateTeamImplementationSettings(
    teamSlug: string,
    teamImplementationSettings: Partial<ITeamImplementationSettings>,
  ) {
    setSettings({
      ...settings,
      teamImplementationSettings: {
        ...settings.teamImplementationSettings,
        [teamSlug]: {
          ...(settings.teamImplementationSettings[teamSlug] ?? { ...DEFAULT_TEAM_IMPLEMENTATION_SETTINGS }),
          ...teamImplementationSettings,
        },
      },
    });
  }

  function updateTeamMetricsSettings(teamSlug: string, teamMetricsSettings: Partial<IMetricsSettings>) {
    setSettings({
      ...settings,
      teamMetricsSettings: {
        ...settings.teamMetricsSettings,
        [teamSlug]: {
          ...(settings.teamMetricsSettings[teamSlug] ?? { ...DEFAULT_METRICS_SETTINGS }),
          ...teamMetricsSettings,
        },
      },
    });
  }

  function updateUserStrategySettings(userGuid: string, userStrategySettings: Partial<IUserStrategySettings>) {
    setSettings({
      ...settings,
      userStrategySettings: {
        ...settings.userStrategySettings,
        [userGuid]: {
          ...(settings.userStrategySettings[userGuid] ?? { ...DEFAULT_USER_STRATEGY_SETTINGS }),
          ...userStrategySettings,
        },
      },
    });
  }

  function updateUserImplementationSettings(
    userGuid: string,
    userImplementationSettings: Partial<IUserImplementationSettings>,
  ) {
    setSettings({
      ...settings,
      userImplementationSettings: {
        ...settings.userImplementationSettings,
        [userGuid]: {
          ...(settings.userImplementationSettings[userGuid] ?? { ...DEFAULT_USER_IMPLEMENTATION_SETTINGS }),
          ...userImplementationSettings,
        },
      },
    });
  }

  function updateUserMetricsSettings(userGuid: string, userMetricsSettings: Partial<IMetricsSettings>) {
    setSettings({
      ...settings,
      userMetricsSettings: {
        ...settings.userMetricsSettings,
        [userGuid]: {
          ...(settings.userMetricsSettings[userGuid] ?? { ...DEFAULT_METRICS_SETTINGS }),
          ...userMetricsSettings,
        },
      },
    });
  }

  function updateUserMeetingsSettings(userGuid: string, userMeetingsSettings: Partial<IUserMeetingsSettings>) {
    setSettings({
      ...settings,
      userMeetingsSettings: {
        ...settings.userMeetingsSettings,
        [userGuid]: {
          ...(settings.userMeetingsSettings[userGuid] ?? DEFAULT_USER_MEETINGS_SETTINGS),
          ...userMeetingsSettings,
        },
      },
    });
  }

  function updateUserFeedbackSettings(userGuid: string, userFeedbackSettings: Partial<IUserFeedbackSettings>) {
    setSettings({
      ...settings,
      userFeedbackSettings: {
        ...settings.userFeedbackSettings,
        [userGuid]: {
          ...(settings.userFeedbackSettings[userGuid] ?? DEFAULT_USER_FEEDBACK_SETTINGS),
          ...userFeedbackSettings,
        },
      },
    });
  }

  function updateSearchSettings(searchSettings: ISearchSettings) {
    setSettings({
      ...settings,
      searchSettings: searchSettings,
    });
  }

  function updateDataGridSettings(dataGridId: string, dataGridSettings: Partial<IDataGridSettings>) {
    setSettings({
      ...settings,
      dataGridSettings: {
        ...settings.dataGridSettings,
        [dataGridId]: {
          ...(settings.dataGridSettings[dataGridId] ?? { ...DEFAULT_DATAGRID_SETTINGS }),
          ...dataGridSettings,
        },
      },
    });
  }

  function updateTableSettings(tableId: string, tableSettings: Partial<ITableSettings>) {
    setSettings({
      ...settings,
      tableSettings: {
        ...settings.tableSettings,
        [tableId]: {
          ...(settings.tableSettings[tableId] ?? { ...DEFAULT_TABLE_SETTINGS }),
          ...tableSettings,
        },
      },
    });
  }

  function updateCoherenceReportSettings(coherenceReportSettings: Partial<ICoherenceReportSettings>) {
    setSettings({
      ...settings,
      coherenceReportSettings: {
        ...settings.coherenceReportSettings,
        ...coherenceReportSettings,
      },
    });
  }

  function updateTeamMeetingSettings(teamSlug: string, teamMeetingSettings: Partial<ITeamMeetingSettings>) {
    setSettings({
      ...settings,
      teamMeetingSettings: {
        ...settings.teamMeetingSettings,
        [teamSlug]: {
          ...(settings.teamMeetingSettings[teamSlug] ?? { ...DEFAULT_TEAM_MEETING_SETTINGS }),
          ...teamMeetingSettings,
        },
      },
    });
  }

  function updateDismissedMeetingNotifications(notifications: IDismissedMeetingNotification[]) {
    setSettings({
      ...settings,
      dismissedMeetingNotifications: notifications,
    });
  }

  const updateFeatureFlag = useCallback(
    (featureFlag: FeatureFlag, value: boolean) => {
      setSettings({
        ...settings,
        featureFlags: {
          ...settings.featureFlags,
          [featureFlag]: value,
        },
      });

      return `Feature flag "${featureFlag}" set to "${value}"`;
    },
    [settings],
  );

  useEffect(() => {
    Object.values(FeatureFlag).forEach((featureFlag) => {
      const toggleName = `toggle${featureFlag.charAt(0).toUpperCase()}${featureFlag.slice(1)}` as const;
      window[toggleName] = () => updateFeatureFlag(featureFlag, !settings.featureFlags[featureFlag]);
    });
  }, [settings.featureFlags, updateFeatureFlag]);

  return (
    <SettingsContext.Provider
      value={{
        ...settings,
        updateHomeSettings,
        updateSideMenuSettings,
        updateDrawerSettings,
        updateTeamOverviewSettings,
        updateTeamStrategySettings,
        updateTeamImplementationSettings,
        updateTeamMetricsSettings,
        updateTeamMeetingSettings,
        updateUserStrategySettings,
        updateUserImplementationSettings,
        updateUserMetricsSettings,
        updateUserMeetingsSettings,
        updateUserFeedbackSettings,
        updateSearchSettings,
        updateFeatureFlag,
        updateDataGridSettings,
        updateTableSettings,
        updateCoherenceReportSettings,
        updateDismissedMeetingNotifications,
      }}
    >
      {props.children}
    </SettingsContext.Provider>
  );
};

export { SettingsContext, SettingsProvider };
